/**
 * How app-api refuses a sign-in credential or an authenticator code, read one way by every app.
 *
 * The reasons are app-api's `AuthVerifyFailureReasonEnum`. All but one ride the sign-in failure
 * body, `{data: {status, pin, authenticator, reason}}`, which every door that checks the member's
 * authenticator answers a refused code with: the emailed-PIN door (`POST /v3/auth/verify`), the
 * Google and Apple doors (`POST /v3/auth/{google,apple}/native`) and turning 2FA off
 * (`DELETE /v3/me/mfa`). `reason` is the only field a client keys on: `pin` means nothing outside
 * the PIN door, and `status` restates what the reason already says. Two ride the v3 error body
 * (`errors[0].reason`) at the Google and Apple doors instead, because they are about the provider
 * side of the sign-in rather than a code: `invalid_identity_token` and
 * `identity_provider_unavailable`.
 */
import { envelopeReason } from "pb-api-errors";
// Keyed by the union, so a reason added to the type fails to compile until it is listed here too.
const SECOND_FACTOR_REASONS = {
    invalid_pin: true,
    invalid_authenticator: true,
    authenticator_required: true,
    authenticator_unavailable: true,
    too_many_attempts: true,
};
/**
 * The reason a sign-in failure body names, whether or not this build knows it; null for any other
 * body.
 *
 * An expired session's 401 is `{"message": "Unauthenticated."}` and never names one, so a 401
 * that does is the API refusing the request on its own terms — a client that renews its session
 * on a 401 must not renew and replay this one, or it presents the same refused code twice.
 */
export function failureReasonOf(body) {
    const reason = body?.data?.reason;
    return typeof reason === "string" ? reason : null;
}
/** The second-factor reason a failure body names, when it is one this contract defines; null otherwise. */
export function secondFactorReasonOf(body) {
    const reason = failureReasonOf(body);
    return reason !== null && Object.hasOwn(SECOND_FACTOR_REASONS, reason) ? reason : null;
}
// Keyed by the union, so a reason added to the type fails to compile until it is listed here too.
const IDENTITY_PROVIDER_REFUSALS = {
    invalid_identity_token: true,
    identity_provider_unavailable: true,
};
/** The provider-side refusal a v3 error body names, when it is one this contract defines; null otherwise. */
export function identityProviderRefusalOf(body) {
    const reason = envelopeReason(body);
    return reason !== null && Object.hasOwn(IDENTITY_PROVIDER_REFUSALS, reason)
        ? reason
        : null;
}
/** What a member is told about a wrong authenticator code, wherever they typed it. */
export const INVALID_AUTHENTICATOR_MESSAGE = "Invalid authenticator code. Please try again.";
/** What a member is told when 2FA is on but the account holds no secret, so no code can ever match. */
export const AUTHENTICATOR_UNAVAILABLE_MESSAGE = "Two-factor authentication isn't set up correctly on your account. Please contact support.";
/**
 * What a member is told about `too_many_attempts` at any door that signs them in.
 *
 * The reason names two holds a client cannot tell apart: the fifth wrong code in a burst, which
 * at the emailed-PIN door also voids the PIN, and the member's daily authenticator ceiling, which
 * every door shares — the PIN door, Google, Apple and turning 2FA off — and which a fresh PIN or a
 * new provider sign-in does not lift. So the sentence offers only what is true under both: waiting,
 * a passkey (which asks for no authenticator code), or support. Requesting a new code or signing in
 * another way that takes a code would meet the daily hold again.
 */
export const TOO_MANY_CODES_MESSAGE = "Too many incorrect codes. Try again later, sign in with a passkey, or contact support.";
